/**
 * Theme Token System
 * 
 * Centralized theme definitions for the UNO game UI.
 * All colors are defined here as design tokens, which are then
 * applied to CSS variables for runtime theme switching.
 */

export type ThemeId = "classic-dark" | "neon-arena" | "casual-party";

export interface ThemeTokens {
  // Core UI colors
  "--bg": string;              // Main background
  "--table": string;           // Game table/board background
  "--text": string;            // Primary text color
  "--accent": string;          // Accent color for buttons/UI elements
  
  // Card colors
  "--card-red": string;
  "--card-yellow": string;
  "--card-green": string;
  "--card-blue": string;
  
  // Optional effects
  "--glow"?: string;          // Glow color for effects
  
  // Effect tokens - Animation & interaction behavior
  "--card-play-animation": string;    // Animation type: "slide" | "glow-pulse" | "bounce"
  "--card-hover-scale": string;        // Scale factor on hover (e.g., "1.03")
  "--card-play-duration": string;      // Duration in ms (e.g., "180ms")
  "--turn-indicator-style": string;    // Style: "glow-soft" | "neon-ring" | "bounce-highlight"
  "--glow-intensity": string;          // Intensity multiplier (e.g., "1.0")
  "--penalty-animation": string;       // Animation type: "pulse" | "neon-flash" | "bounce"
}

export const THEMES: Record<ThemeId, ThemeTokens> = {
  "classic-dark": {
    "--bg": "#0a0a0f",
    "--table": "linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 30%, #0f1419 70%, #050508 100%)",
    "--text": "#ffffff",
    "--accent": "#667eea",
    "--card-red": "#E53935",
    "--card-yellow": "#FDD835",
    "--card-green": "#43A047",
    "--card-blue": "#1E88E5",
    "--glow": "#667eea",
    "--card-play-animation": "slide",
    "--card-hover-scale": "1.03",
    "--card-play-duration": "180ms",
    "--turn-indicator-style": "glow-soft",
    "--glow-intensity": "1.0",
    "--penalty-animation": "pulse",
  },
  "neon-arena": {
    "--bg": "#000000",
    "--table": "linear-gradient(180deg, #000000 0%, #1a0033 30%, #330033 70%, #000000 100%)",
    "--text": "#ffffff",
    "--accent": "#00ffff",
    "--card-red": "#ff0066",
    "--card-yellow": "#ffff00",
    "--card-green": "#00ff66",
    "--card-blue": "#0066ff",
    "--glow": "#00ffff",
    "--card-play-animation": "glow-pulse",
    "--card-hover-scale": "1.06",
    "--card-play-duration": "220ms",
    "--turn-indicator-style": "neon-ring",
    "--glow-intensity": "1.5",
    "--penalty-animation": "neon-flash",
  },
  "casual-party": {
    // Primary background: solid warm cream (paper-like, no gradients)
    "--bg": "#faf8f3",
    // Table background: flat pastel color (clearly separates from screen background)
    // Slightly darker than bg for clear definition, but still warm and calm
    "--table": "#f0e8d8",
    // Primary text: dark brown/charcoal (high contrast, WCAG AA compliant)
    "--text": "#2c2416",
    // Accent: warm coral (used ONLY for active turn, primary buttons, selection)
    "--accent": "#ff7a8a",
    // Card colors: muted, warm, same brightness level, clearly distinguishable
    // Red: muted warm red (not neon, readable on cream table)
    "--card-red": "#d85a5a",
    // Yellow: warm beige-yellow (readable on cream, not pure yellow)
    "--card-yellow": "#d4a574",
    // Green: muted warm green (balanced with others)
    "--card-green": "#7fb069",
    // Blue: muted warm blue (balanced with others)
    "--card-blue": "#6b9bd1",
    // Glow: reduced intensity (50% of original) - softer version of accent
    "--glow": "#ff7a8a",
    "--card-play-animation": "bounce", // ease-out + small bounce
    "--card-hover-scale": "1.05",
    "--card-play-duration": "280ms", // Slightly longer for bounce effect
    "--turn-indicator-style": "bounce-highlight",
    "--glow-intensity": "0.3", // Reduced from 0.6 to 0.3 (50% reduction)
    "--penalty-animation": "bounce",
  },
};

/**
 * Default theme fallback
 */
export const DEFAULT_THEME: ThemeId = "classic-dark";
